import Backbone from 'backbone';
import * as d3 from 'd3';
import $ from 'jquery';
import React from 'react';
import ReactDOM from 'react-dom/client';
import _ from 'underscore';
import {v4 as uuidv4} from 'uuid';

import DigestTabState from 'analyses/DigestTabState';
import VirtualDigestMenuTemplate from 'analyses/VirtualDigestMenuTemplate';
import VirtualDigestTabState, {Ladder} from 'analyses/VirtualDigestTabState';
import VirtualDigestTabTemplate, {
  VirtualDigestTabLegendItem,
  VirtualDigestTabProps,
} from 'analyses/VirtualDigestTabTemplate';
import * as benchlingUtil from 'BenchlingUtil';
import {Sequence as BioJSSequence} from 'biojs/Sequence';
import * as util from 'biojs/Util';
import BufferOptionsView from 'buffers/BufferOptionsView';
import BufferTabView from 'buffers/BufferTabView';
import * as selectioncontextmenu from 'components/SelectionContextMenu';
import {Sequence} from 'components/Sequence';
import FileStates from 'FileStates';
import * as ladders from 'molbio/virtual-digests/Ladders';
import {pushToast} from 'nav/toasts/ToastActions';
import {ToastType} from 'nav/toasts/ToastConstants';
import notifications, {FOREVER_DURATION} from 'notifications';
import {renderToDOM, unmountRootsAsync} from 'redux-stores/ReduxDOMRender';
import getTestProps from 'test/lib/cypress/getTestProps';
import {toFilename} from 'util/FileUtil';
import {appendSVGToSVG, getPNGFileForSVG} from 'util/SVGUtil';

function getLegendInfo(model: VirtualDigestTabState): Array<VirtualDigestTabLegendItem> {
  const lanes = [];
  const ladder = model.ladders().find((ladder) => ladder.id.toString() === model.get('ladderId'))!;
  lanes.push({index: 'Ladder' as const, title: ladder.name});

  model.get('digests').each((digest, index) => {
    const sequence = digest.get('sequence');
    const names = digest
      .get('analysis')
      .get('enzymes')!
      .map((id) => sequence.get('enzymes').get(id).get('name'));
    lanes.push({
      index: `${index + 1}`,
      title: `${sequence.get('_sequence').get('name')} - ${names.join(' ')}`,
    });
  });
  return lanes;
}

class OptionsView extends BufferOptionsView<VirtualDigestTabState> {
  svgReactRoot: ReactDOM.Root | null = null;
  template!: () => string;

  static initClass(): void {
    this.prototype.className = 'navbar';
    this.prototype.template = VirtualDigestMenuTemplate;
  }

  override events(): Backbone.EventsHash {
    return {
      'change select': 'changeLadder',
      'click .download-btn': 'downloadPNG',
    };
  }

  async copyPNG(imageURL: string): Promise<void> {
    const makeImagePromise = async (): Promise<Blob> => {
      const response = await fetch(imageURL);
      const blob = await response.blob();
      return new Blob([blob], {type: 'image/png'});
    };

    // Firefox does not allow passing a Promise to ClipboardItem but Safari requires it
    // See https://bugzilla.mozilla.org/show_bug.cgi?id=1619947
    if (benchlingUtil.firefox) {
      const response = await fetch(imageURL);
      const blob = await response.blob();
      await navigator.clipboard.write([
        new window.ClipboardItem({'image/png': new Blob([blob], {type: 'image/png'})}),
      ]);
    } else {
      await navigator.clipboard.write([new window.ClipboardItem({'image/png': makeImagePromise()})]);
    }

    pushToast({
      message: 'PNG copied',
      type: ToastType.SUCCESS,
    });
  }

  createSVG(): SVGSVGElement {
    const legendRowHeight = 24;
    const legend = getLegendInfo(this.model);

    // Note this is definitely not great coupling, but the alternative is factoring out
    // all of the SVG rendering code from the view below.
    // @ts-expect-error BufferViewLayout is set in BufferContentView.update
    const digestSVGElement = this.model.get('bufferViewLayout').get('el')!.find('svg')[0].cloneNode(true);
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');

    // Split legend items into lines when the text is too long
    // Maximum length is 60 characters plus 10 characters for each digest column after the fourth
    const maxLegendChars = 60 + 10 * Math.max(legend.length - 4, 0);
    const legendRows = legend.flatMap(({index, title}) => {
      const rowSplitRegex = new RegExp(`(?:\\s|^)(\\S.{0,${maxLegendChars}}|\\S+)(?!\\S)`);
      return title
        .split(rowSplitRegex)
        .map((titlePart) => titlePart.trim())
        .filter((titlePart) => titlePart !== '')
        .map((titlePart, idx) => ({
          index: idx === 0 ? index : null,
          title: titlePart,
        }));
    });

    const pngHeight =
      parseInt(digestSVGElement.getAttribute('height'), 10) + (legendRows.length + 1) * legendRowHeight;
    const pngWidth = Math.max(parseInt(digestSVGElement.getAttribute('width'), 10), 500);

    // React 18 owns all children of its root node. appendSVGToSVG adds the digest as a sibling of
    // the legend, so the root must be a dedicated group—not the outer <svg>—or a deferred React
    // commit (e.g. while getPNGFileForSVG awaits font/CSS) can remove the digest markup.
    const legendContainer = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    svg.appendChild(legendContainer);

    this.svgReactRoot = renderToDOM(
      <>
        <rect fill="white" width={pngWidth} height={pngHeight} />
        {legendRows.map(({index, title}, rowNumber) => (
          <g key={rowNumber} transform={`translate(10, ${legendRowHeight * (rowNumber + 1)})`}>
            {index && <text transform="translate(0, 0)">{index}</text>}
            <text transform={`translate(80, ${index ? 0 : -4})`}>{title}</text>
          </g>
        ))}
      </>,
      legendContainer,
      this.svgReactRoot
    );
    appendSVGToSVG({
      parentSVG: svg,
      childSVG: digestSVGElement,
      transform: `translate(0, ${(legendRows.length + 1) * legendRowHeight})`,
    });

    return svg;
  }

  async downloadPNG(): Promise<void> {
    const toastId = uuidv4();
    pushToast({
      id: toastId,
      message: 'Creating PNG...',
      type: ToastType.INFO,
      duration: FOREVER_DURATION,
    });
    const digestSVG = this.createSVG();

    // Safari blocks calls to window.open during async calls, so we open the window here first
    // then set the location when the download url is ready.
    let windowReference;
    if (benchlingUtil.safari) {
      windowReference = window.open();
    }

    try {
      const pngURL = await getPNGFileForSVG(
        digestSVG,
        toFilename(`virtual_digest_sequence_${this.model.get('sequence').name()}`, 'png')
      );

      if (benchlingUtil.safari) {
        // @ts-expect-error TS thinks windowReference can be null in this case, but oxlint thinks it can't
        windowReference.location = pngURL;
      } else {
        window.open(pngURL);
      }
      pushToast({
        id: toastId,
        message: 'PNG downloaded',
        type: ToastType.SUCCESS,
        duration: FOREVER_DURATION,
        ...(window.ClipboardItem &&
          // @ts-expect-error navigator.clipboard.write is always defined these days, but was not always
          // defined at the time this was originally written
          navigator.clipboard.write && {
            actionText: 'Copy to clipboard',
            onActionClick: () => {
              this.copyPNG(pngURL);
            },
          }),
      });
    } catch (error) {
      pushToast({
        id: toastId,
        message: notifications.getFriendlyError(error)!,
        type: ToastType.DANGER,
      });
      throw error;
    }
  }

  // TODO: Was this intended to be void and never cleaned up after decaffeinate?
  changeLadder(): VirtualDigestTabState {
    return this.model.set({ladderId: this.$el.find('select option:selected').val()});
  }

  // TODO: Was this intended to be void and never cleaned up after decaffeinate?
  addLadderOption(ladder: Ladder): JQuery {
    // @ts-expect-error `ladder.id` can be a number, which is not allowed per builtin types but works fine
    return this.$('select').append(new Option(ladder.name, ladder.id));
  }

  override removeAsync(callback: () => void): this {
    unmountRootsAsync([this.svgReactRoot], () => {
      this.svgReactRoot = null;
      super.remove();
      callback();
    });
    return this;
  }

  override render(): this {
    this.$el.html(this.template());
    const allLadders = this.model.ladders();
    for (const ladder of Array.from(allLadders)) {
      this.addLadderOption(ladder);
    }
    const personalLadders = new ladders.Collection();
    personalLadders.fetch().done(() =>
      personalLadders.each((ladder) => {
        const ladderDict = {
          id: ladder.id,
          name: ladder.get('name'),
          bands: ladder
            .bandSizes()
            .slice()
            .sort((a, b) => a - b),
        };
        // Have to do deep comparison of ladderDict to membership in allLadders to determine if it should
        // be added to allLadders.
        const isNewCustomLadder = !allLadders.some(
          (item) =>
            item.id === ladderDict.id &&
            item.name === ladderDict.name &&
            item.bands.every((val, index) => val === ladderDict.bands[index])
        );
        if (isNewCustomLadder) {
          allLadders.push(ladderDict);
          this.addLadderOption(ladderDict);
        }
      })
    );
    return this;
  }
}

OptionsView.initClass();

class TabView extends BufferTabView<VirtualDigestTabState> {
  fileStates!: FileStates;
  sequence!: Sequence;
  template!: (props: VirtualDigestTabProps) => string;
  y!: d3.ScaleLogarithmic<number, number>;

  static override initClass(): void {
    this.prototype.id = 'virtual-gel';
    this.prototype.template = VirtualDigestTabTemplate;
    this.prototype.className = 'buffer-tab';

    this.prototype.attributes = {
      ...this.prototype.attributes,
      ...getTestProps({component: 'VirtualDigestView', element: 'root'}),
    };
  }

  override initialize(options: Backbone.ViewOptions<VirtualDigestTabState>): void {
    this.sequence = this.model.get('sequence');
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.model, 'change:ladderId', this.render);
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.model.get('digests'), 'add remove', this.render);
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.model.get('digests'), 'selectedFragmentChange', this.update);
    const listenToDigest = (digest: DigestTabState): void => {
      // eslint-disable-next-line @typescript-eslint/unbound-method
      this.listenTo(digest.get('analysis'), 'change:fragments', this.render);
    };
    this.model.get('digests').each(listenToDigest);
    this.listenTo(this.model.get('digests'), 'add', listenToDigest);
    this.listenTo(
      this.model.get('digests'),
      'remove',
      // eslint-disable-next-line no-restricted-syntax
      _.bind(function (digest: DigestTabState) {
        // @ts-expect-error shadows outer this
        return this.stopListening(digest);
      }, this)
    );
    this.fileStates = FileStates.get();
  }

  update(): void {
    // There are a handful of cases below where we imperatively modify a d3 attribute from a handler and we
    // need `this` to be the element whose handler is being invoked AND we need access to the larger view.
    // I am so sorry for making you think about this.
    const that = this;
    const layout = that.model.get('layout');
    // eslint-disable-next-line no-restricted-syntax
    const ladder = _.find(
      that.model.ladders(),
      (ladder) => ladder.id.toString() === that.model.get('ladderId')
    )!;

    that.y = d3
      .scaleLog()
      .domain([ladder.bands[0], ladder.bands[ladder.bands.length - 1]])
      .rangeRound([layout.gel.height - layout.gel.padding - layout.band.height, layout.gel.padding])
      .clamp(true);

    // N.B.: `ladder` is a `Ladder` and `that.model.get('digests').models` are `DigestTabState`s
    const data = [ladder, ...that.model.get('digests').models];

    const header = d3.select(that.el).select('g.header').selectAll('text').data(data);
    header
      .join('svg:text')
      .text((_, i) => {
        if (i === 0) {
          return 'Ladder';
        } else {
          return i;
        }
      })
      .attr('x', (_, i) => layout.lane.width * i + layout.lane.width / 2)
      .attr('y', layout.header.height / 2)
      .attr('text-anchor', 'middle');

    const ladderSvg = d3.select(that.el).select('g.ladder-labels').selectAll('text').data(ladder.bands);
    ladderSvg
      .join('svg:text')
      .text((d) => util.formatBaseCount(d))
      .attr('transform', (d) => `translate(0,${that.y(d) + Math.floor(layout.type.height / 3)})`);

    let lane = d3.select(that.el).select('g.gel').selectAll('g.lane').data(data);

    const laneEnter = lane
      .enter()
      .append('svg:g')
      .attr('class', (_, i) => {
        if (i === 0) {
          return 'lane ladder';
        } else {
          return 'lane';
        }
      })
      .attr('transform', (_, i) => `translate(${layout.lane.width * i},0)`);

    laneEnter
      .append('svg:g')
      .attr('transform', `translate(${(layout.lane.width - layout.band.width) / 2},0)`)
      .attr('class', 'bands');

    lane = laneEnter.merge(lane);

    const bands = lane
      .select('g.bands')
      .selectAll('rect')
      // @ts-expect-error d3 types / generics are not flexible enough to handle a union of disjoint types in
      // data
      .data((d: Ladder | DigestTabState): Array<BioJSSequence> | Array<number> => {
        if (d instanceof DigestTabState) {
          return d.get('analysis').get('fragments')!.models;
        } else {
          return d.bands;
        }
      });

    const handleClick = (d: BioJSSequence, rightClick: boolean = false): void => {
      if (that.model.get('digests').at(0).get('sequence').fileState() == null) {
        return;
      }
      let selectedDigest = null;
      that.model.get('digests').each((digest) => {
        digest
          .get('analysis')
          .get('fragments')!
          .each((fragment) => {
            if (fragment === d) {
              selectedDigest = digest;
            } else {
              fragment.set({selected: false});
            }
          });
      });
      const target = selectedDigest!.get('sequence');
      that.fileStates.selectFragment(target, d, rightClick);
    };

    bands
      .join('rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', layout.band.width)
      .attr('height', layout.band.height)
      .attr('fill', (d: number | BioJSSequence) => {
        if (d instanceof BioJSSequence) {
          if (d.get('selected')) {
            return layout.band.active.fill;
          } else {
            return layout.band.fill;
          }
        } else {
          return layout.band.fill;
        }
      })
      .attr(
        'transform',
        (d: number | BioJSSequence) =>
          `translate(0,${d instanceof BioJSSequence ? that.y(d.get('length')) : that.y(d)})`
      )
      .on('click', (_, d: number | BioJSSequence) => {
        if (d instanceof BioJSSequence) {
          handleClick(d);
        }
      })
      .on('mouseover', function (_, d: number | BioJSSequence): void {
        if (d instanceof BioJSSequence) {
          $(that.el)
            .find('dl.highlighted dd')
            .text(util.formatBaseCount(d.get('length')));
          if (!d.get('selected')) {
            d3.select(this).attr('fill', layout.band.active.fill);
          }
        }
      })
      .on('mouseleave', function (_, d: number | BioJSSequence): void {
        if (d instanceof BioJSSequence) {
          let length = '--';
          const selected = d.collection.find((fragment) => fragment.get('selected'));
          if (selected != null) {
            length = util.formatBaseCount(selected.get('length'));
          }
          $(that.el).find('dl.highlighted dd').text(length);
          if (!d.get('selected')) {
            d3.select(this).attr('fill', layout.band.fill);
          }
        }
      })
      .on('contextmenu', (e, d): void => {
        if (that.model.get('digests').at(0).get('sequence').fileState() == null) {
          return;
        }
        const digest = that.model.get('digests').find(
          (digest) =>
            digest
              .get('analysis')
              .get('fragments')!
              .find((fragment) => fragment === d) != null
        );
        if (
          d instanceof BioJSSequence &&
          selectioncontextmenu.open(e, digest.get('sequence'), ['copy', 'copySpecial'])
        ) {
          handleClick(d, true);
        }
      })
      .each((d: number | BioJSSequence): void => {
        if (d instanceof BioJSSequence && d.get('selected') != null && d.get('selected')) {
          $(that.el)
            .find('dl.highlighted dd')
            .text(util.formatBaseCount(d.get('length')));
        }
      });

    lane.exit().remove();
  }

  override render(): this {
    this.$el.empty();

    const legend = getLegendInfo(this.model);
    this.$el.append(this.template({legend}));

    const layout = this.model.get('layout');

    d3.select(this.el)
      .append('svg')
      .attr('height', layout.padding + layout.gel.height + layout.header.height)
      .attr('width', layout.padding + layout.ladder.width + legend.length * layout.lane.width)
      .attr('data-test-component', 'VirtualDigestView')
      .attr('data-test-element', 'svgRoot')
      .append('g')
      .attr('transform', `translate(${layout.padding},${layout.padding})`);

    const container = d3.select(this.el).select('svg').select('g');
    container.append('g').attr('class', 'header').attr('transform', `translate(${layout.ladder.width},0)`);
    container
      .append('g')
      .attr('class', 'ladder-labels')
      .attr('transform', `translate(0,${layout.header.height})`);

    const gelContainer = container
      .append('g')
      .attr('class', 'gel-container')
      .attr('transform', `translate(${layout.ladder.width},${layout.header.height})`);

    gelContainer
      .append('svg:rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', legend.length * layout.lane.width)
      .attr('height', layout.gel.height)
      .attr('fill', layout.gel.fill);

    gelContainer.append('svg:g').attr('class', 'gel');

    this.update();
    return this;
  }

  override removeAsync(callback: () => void): this {
    super.remove();
    callback();
    return this;
  }
}

TabView.initClass();

export {OptionsView, TabView};
